import React from "react";
import { AlertTriangle } from "lucide-react";

/**
 * App-wide error boundary. Catches render errors in the subtree and shows
 * a friendly recovery card instead of a white screen.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, info);
  }
  reset = () => this.setState({ hasError: false, error: null });
  render() {
    if (this.state.hasError) {
      return (
        <div
          data-testid="error-boundary"
          className="min-h-[60vh] flex items-center justify-center p-6 bg-[#F8FAFC]"
        >
          <div className="max-w-md w-full bg-white border border-[#E2E8F0] p-8 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-amber-50 flex items-center justify-center mb-5">
              <AlertTriangle size={20} className="text-amber-600" />
            </div>
            <h2 className="font-serif text-[#12283F] text-2xl mb-2">
              Something went wrong on this screen.
            </h2>
            <p className="text-sm text-[#64748B] leading-relaxed mb-6">
              The error has been logged. You can try again, return home, or
              continue to another section.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={this.reset}
                data-testid="error-boundary-retry"
                className="btn-primary"
              >
                Try again
              </button>
              <a href="/" className="btn-outline" data-testid="error-boundary-home">
                Go home
              </a>
            </div>
            {this.state.error && (
              <details className="mt-6 text-left text-[11px] text-[#94A3B8]">
                <summary className="cursor-pointer">Technical detail</summary>
                <pre className="mt-2 whitespace-pre-wrap break-words">
                  {String(this.state.error?.message || this.state.error)}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
